// file: tracking.jsx
import React from "react";
import styles from "./tracking.module.scss";

const Tracking = ({ trackingData, status, cartBundleData }) => {
  const shipment = trackingData?.ShipmentData?.[0]?.Shipment;
  const scans = shipment?.Scans || [];

  // -------------------------- DATE FORMATTER --------------------------
  const formatDate = (dt) =>
    new Date(dt).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatDateWithoutTime = (dt) =>
    new Date(dt).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  // -------------------------- CLEAN TEXT MAPPING --------------------------
  const mapInstruction = (instruction = "") => {
    const text = instruction.toLowerCase();

    if (text.includes("picked up")) return "Shipment picked up";
    if (text.includes("origin center"))
      return "Shipment arrived at departure facilities";
    if (text.includes("vehicle departed"))
      return "Shipment left departure facilities";
    if (text.includes("received at facility"))
      return "Shipment Received at Facility";

    return instruction;
  };

  // -------------------------- ALLOWED STEPS FILTER --------------------------
  const importantSteps = [
    "picked up",
    "origin center",
    "vehicle departed",
    "received at facility",
  ];

  const shippedEvents = scans.filter((s) => {
    const txt = s?.ScanDetail?.Instructions?.toLowerCase() || "";
    return importantSteps.some((step) => txt.includes(step));
  });

  // --------------------------- DOT COLOR LOGIC ---------------------------
  const getDotClass = (section) => {
    if (section === "confirmed") {
      if (status === 0 || status === 1) return styles.dotGreen;
      if (status >= 2) return styles.dotCompleted;
    }

    if (section === "shipped") {
      if (status === 1) return styles.dotBlueBreath;
      if (status >= 2) return styles.dotCompleted;
    }

    if (section === "outForDelivery") {
      if (shipment?.status?.Status === "At Destination Facility") {
        return styles.dotBlueBreath;
      } else if (shipment?.status?.Status === "Out for Delivery") {
        return styles.dotCompleted;
      } else {
        return styles.dotInactive;
      }
    }

    if (section === "delivered") {
      if (status === 3 || shipment?.Status?.Status === "Delivered") {
        return styles.dotGreen;
      } else if (shipment?.status?.Status === "Out for Delivery") {
        return styles.dotBlueBreath;
      } else {
        return styles.dotInactive;
      }
    }

    return styles.dotInactive;
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Track</h2>

      <div className={styles.wrapper}>
        {/* -------------------- ORDER CONFIRMED -------------------- */}
        <div className={styles.section}>
          <div className={styles.statusHeader}>
            <span
              className={`${styles.statusDot} ${getDotClass("confirmed")}`}
            ></span>
            <h3 className={styles.statusTitle}>Order Confirmed</h3>
            <span className={styles.statusDate}>
              {formatDateWithoutTime(cartBundleData?.orders[0]?.createdAt)}
            </span>
          </div>

          <div className={styles.statusDetails}>
            <p className={styles.statusInfo}>Your Order has been placed.</p>
            <span>
              {formatDateWithoutTime(cartBundleData?.orders[0]?.createdAt)}
            </span>

            {status >= 1 && (
              <>
                <p className={styles.statusInfo}>Order has been packed.</p>
                <p className={styles.statusInfo}>
                  Your item has been picked up by delivery partner.
                </p>
              </>
            )}
          </div>
        </div>

        {/* -------------------- SHIPPED -------------------- */}
        <div className={styles.section}>
          <div className={styles.statusHeader}>
            <span
              className={`${styles.statusDot} ${getDotClass("shipped")}`}
            ></span>
            <h3 className={styles.statusTitle}>
              In-Transit <small>(shipped)</small>
            </h3>

            {status >= 2 && (
              <span className={styles.statusDate}>
                {formatDateWithoutTime(shipment?.PickUpDate)}
              </span>
            )}
          </div>

          {status >= 2 && (
            <div className={styles.statusDetails}>
              {shippedEvents.length > 0 &&
                shippedEvents.map((item, index) => {
                  const d = item.ScanDetail;
                  return (
                    <div key={index} className={styles.eventItem}>
                      <p className={styles.statusInfo}>
                        {mapInstruction(d?.Instructions)}
                      </p>
                      <span>{d?.ScannedLocation}</span>
                      <span>{formatDateWithoutTime(d?.ScanDateTime)}</span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* -------------------- OUT FOR DELIVERY -------------------- */}
        <div className={styles.section}>
          <div className={styles.statusHeader}>
            <span
              className={`${styles.statusDot} ${getDotClass("outForDelivery")}`}
            ></span>
            <h3 className={styles.statusTitle}>Out For Delivery</h3>

            {shipment?.FirstAttemptDate && (
              <span className={styles.statusDate}>
                {formatDateWithoutTime(shipment?.FirstAttemptDate)}
              </span>
            )}
          </div>

          {shipment?.FirstAttemptDate && (
            <div className={styles.statusDetails}>
              <p className={styles.statusInfo}>Your item is out for delivery</p>
              <span>{formatDateWithoutTime(shipment?.FirstAttemptDate)}</span>
            </div>
          )}
        </div>

        {/* -------------------- DELIVERED -------------------- */}
        <div className={styles.sectionDelivered}>
          <div className={styles.statusHeader}>
            <span
              className={`${styles.statusDot} ${getDotClass("delivered")}`}
            ></span>
            <h3 className={styles.statusTitle}>Delivered</h3>

            {shipment?.Status?.Status === "Delivered" && (
              <span className={styles.statusDate}>
                {formatDateWithoutTime(shipment?.DeliveryDate)}
              </span>
            )}
          </div>

          <div className={styles.statusDetails}>
            {shipment?.Status?.Status === "Delivered" && (
              <>
                <p className={styles.statusInfo}>
                  Your item has been delivered
                </p>
                <span>{formatDateWithoutTime(shipment?.DeliveryDate)}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tracking;
